<template>
    <v-dialog v-model="show" max-width="1200px">
        <v-card>
            <v-card-title>
                <div>
                    <v-tabs v-model="tab" v-if="enableImprove && enableSuggest">
                        <v-tab v-show="enableImprove">
                            Improve
                            <v-tooltip top>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon
                                        v-bind="attrs"
                                        v-on="on"
                                        color="primary"
                                        class="ml-2"
                                        small
                                        >mdi-help-circle-outline
                                    </v-icon>
                                </template>
                                <span>Improve existing content</span>
                            </v-tooltip>
                        </v-tab>
                        <v-tab v-show="enableSuggest">
                            Suggest
                            <v-tooltip top>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon
                                        v-bind="attrs"
                                        v-on="on"
                                        color="primary"
                                        class="ml-2"
                                        small
                                        >mdi-help-circle-outline
                                    </v-icon>
                                </template>
                                <span>Suggest alternative content</span>
                            </v-tooltip>
                        </v-tab>
                    </v-tabs>
                    <h2 class="subtitle-1" v-else-if="enableImprove">Improve Content</h2>
                    <h2 class="subtitle-1" v-else-if="enableSuggest">Suggest Content</h2>
                </div>
                <v-spacer></v-spacer>
                <v-btn icon title="close" class="btn-background" @click="show = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-card-text v-if="loading" class="d-flex text-center align-center flex-column pt-6">
                <v-progress-circular :size="50" color="primary" indeterminate style="top: 45%"
                    class="mb-3"></v-progress-circular>
                <p>
                    <span v-html="improvementsDisclaimer"></span>
                </p>
            </v-card-text>
            <v-card-text v-else class="pb-4">
                <v-tabs-items v-model="tab">
                    <v-tab-item>
                        <v-row v-if="remainingIssuesWarning && improvementTextStatus && improvementTextStatus.errors.length + improvementTextStatus.warnings.length > 0">
                            <v-col>
                                <v-alert class="mb-0" text type="warning"><span v-html="remainingIssuesWarning"></span></v-alert>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="6" v-if="textStatus">
                                <h3>Original Text</h3>
                                <div v-html="textStatus.highlightedText"></div>
                                <h3 class="mt-2" v-if="textStatus.errors.length || textStatus.warnings.length">Issues</h3>
                                <div v-for="(e, i) in textStatus.errors" :key="'error' + i">
                                    <v-icon color="error" style="font-size: 18px">mdi-alert</v-icon>
                                    {{ e.category }} - <span v-html="e.description"></span>
                                </div>
                                <div v-for="(e, i) in textStatus.warnings" :key="'warn' + i">
                                    <v-icon color="warning" style="font-size: 18px">mdi-alert</v-icon>
                                    {{ e.category }} - <span v-html="e.description"></span>
                                </div>
                            </v-col>
                            <v-col cols="6" v-if="improvementTextStatus">
                                <h3>Suggested Text</h3>
                                <div v-html="improvementTextStatus.highlightedText"></div>
                                <h3 class="mt-2"
                                    v-if="improvementTextStatus.errors.length || improvementTextStatus.warnings.length">Issues</h3>
                                <div v-for="(e, i) in improvementTextStatus.errors" :key="'error' + i">
                                    <v-icon color="error" style="font-size: 18px">mdi-alert</v-icon>
                                    {{ e.category }} - <span v-html="e.description"></span>
                                </div>
                                <div v-for="(e, i) in improvementTextStatus.warnings" :key="'warn' + i">
                                    <v-icon color="warning" style="font-size: 18px">mdi-alert</v-icon>
                                    {{ e.category }} - <span v-html="e.description"></span>
                                </div>

                                <h3>Refine with additional instructions
                                <v-btn icon @click="additionalInstructions.push('')" title="Add instruction"><v-icon>mdi-plus-circle-outline</v-icon></v-btn></h3>

                                <v-row v-for="(ins, insi) in additionalInstructions" :key="'inst' + insi" class="mx-1">
                                    <v-text-field placeholder="Add instruction to AI"
                                        v-model="additionalInstructions[insi]"
                                        hide-details>
                                    </v-text-field>
                                </v-row>
                            </v-col>
                        </v-row>
                    </v-tab-item>
                    <v-tab-item>
                        <v-row>
                            <v-col cols="4" class="text-right">
                                <v-text-field
                                    dense
                                    class="mt-2"
                                    outlined
                                    label="Job Title"
                                    v-model="generateRequest.jobTitle"
                                    v-on:keyup.enter="suggestContent()"
                                    hide-details
                                    clearable
                                ></v-text-field>
                                <v-text-field
                                    v-if="enableGenerateJobFamily"
                                    class="mt-2"
                                    dense
                                    outlined
                                    label="Job Family"
                                    placeholder="Any"
                                    v-model="generateRequest.jobFamily"
                                    v-on:keyup.enter="suggestContent()"
                                    hide-details
                                    clearable
                                ></v-text-field>
                                <v-select
                                    v-if="enableGenerateLevelling"
                                    class="mt-2"
                                    dense
                                    outlined
                                    label="Job Level"
                                    placeholder="Any"
                                    v-model="generateRequest.jobLevel"
                                    v-on:keyup.enter="suggestContent()"
                                    :items="contentGenerationSettings.levels"
                                    hide-details
                                    clearable
                                ></v-select>
                                <v-btn
                                    class="mt-2"
                                    color="primary"
                                    @click="suggestContent()">
                                    Generate
                                </v-btn>
                            </v-col>
                            <!-- <v-col cols="8">
                                <h3 class="mb-2">Add additional instructions
                                <v-btn icon @click="generateRequest.additionalInstructions.push('')" title="Add instruction"><v-icon>mdi-plus-circle-outline</v-icon></v-btn></h3>

                                <v-row v-for="(ins, insi) in generateRequest.additionalInstructions" :key="'inst' + insi" class="mx-0">
                                    <v-text-field placeholder="Add instruction to AI"
                                        v-model="generateRequest.additionalInstructions[insi]"
                                        dense
                                        outlined
                                        hide-details
                                        append-icon="delete"
                                        @click:append="generateRequest.additionalInstructions.splice(insi, 1)"
                                        >
                                    </v-text-field>
                                </v-row>
                            </v-col> -->
                            <v-col cols="8">
                                <h3>{{ suggestions.length === 1 ? 'Suggested Text' : 'Suggestions' }}</h3>
                                <div v-if="suggestions.length === 1">
                                    <span v-html="suggestions[0]"></span>
                                </div>
                                <template v-else>
                                    <div v-for="(t, ti) in suggestions" :key="`sts_${ti}`">
                                        <v-checkbox :label="t" v-model="selectedSuggestions" :value="t"></v-checkbox>
                                    </div>
                                </template>
                            </v-col>
                        </v-row>
                    </v-tab-item>
                </v-tabs-items>
            </v-card-text>
            <v-card-actions class="px-6 pb-4">
                <v-btn outlined color="primary" @click="show = false">Cancel</v-btn>
                <v-spacer></v-spacer>
                <div v-if="tab === 0">
                    <v-btn outlined color="primary" class="ml-2" @click="improveContent">Retry</v-btn>
                    <v-btn color="primary" class="ml-2" @click="acceptImprovement">Accept Suggestion</v-btn>
                </div>
                <div v-else-if="tab === 1 && suggestions.length !== 0">
                    <!-- <v-btn outlined color="primary" class="ml-2" @click="suggestContent">Retry</v-btn> -->
                    <v-btn color="primary" class="ml-2" v-if="suggestions.length > 1" @click="acceptSuggestion" :disabled="selectedSuggestions.length === 0">Add Selected</v-btn>
                    <v-btn color="primary" class="ml-2" v-else-if="!data.text" @click="acceptSuggestion" :disabled="selectedSuggestions.length === 0">Use Selected</v-btn>
                    <template v-else>
                        <v-btn color="warning" class="ml-2" @click="acceptSuggestion" :disabled="selectedSuggestions.length === 0">Replace with Suggested</v-btn>
                        <v-btn color="primary" class="ml-2" @click="appendSuggestion" :disabled="selectedSuggestions.length === 0">Append Suggested</v-btn>
                    </template>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import viewUtils from './utils';
import utils from "@/common/utils.js";
import { mapState } from "vuex";
import axios from "axios";

export default {
    name: 'AIImprove',
    props: {
        data: Object,
    },
    data: function () {
        return {
            loading: false,
            documentLanguage: { text: "English", value: "en", flagCode: "gb" },
            tab: 0,
            show: false,
            textStatus: null,
            improveResponseData: null,
            improvementTextStatus: null,
            additionalInstructions: [],
            generateRequest: {
                jobTitle: "",
                jobFamily: "",
                jobLevel: "",
                additionalInstructions: []
            },
            contentGenerationSettings: null,
            suggestResponseData: null,
            suggestions: [],
            selectedSuggestions: [],
        }
    },
    components: {
    },
    watch: {
        data(newVal) {
            if (newVal) {
                this.init(newVal);
            }
        },
        show(val) {
            if (!val) {
                this.textStatus = null;
                this.improveResponseData = null;
                this.improvementTextStatus = null;
                this.additionalInstructions.splice(0);
                this.generateRequest.jobTitle = "";
                this.generateRequest.jobFamily = "";
                this.generateRequest.jobLevel = "";
                this.generateRequest.additionalInstructions.splice(0);
                this.suggestResponseData = null;
                this.suggestions.splice(0);
                this.selectedSuggestions.splice(0);
                this.contentGenerationSettings = null;

                this.$emit("close");
            }
        },
        tab(val) {
            if (this.show && val === 0 && !this.improveResponseData)
                this.improveContent();
            else if (this.show && val === 1 && !this.suggestResponseData)
                this.suggestContent();
        }
    },
    mounted() {

    },
    created() {
        if (this.data) {
            this.init(this.data);
        }
    },
    computed: {
        ...mapState({
            config: (state) => state.settings.config
        }),
        remainingIssuesWarning() {
            if (this.config.contentGenerationConfig.remainingIssuesWarning !== undefined)
                return this.config.contentGenerationConfig.remainingIssuesWarning;
            else
                return "Unfortunately we were unable to remove all the issues whilst keeping the same meaning.";
        },
        improvementsDisclaimer() {
            if (this.config.contentGenerationConfig.improvementsDisclaimer !== undefined)
                return this.config.contentGenerationConfig.improvementsDisclaimer;
            else
                return "Working on AI suggestions to improve your content.";
        },
        enableImprove() {
            return this.data?.text && this.data?.enableImprove;
        },
        enableSuggest() {
            return !!this.data?.partType?.tmpl_part_metadata.suggestion_part
        },
        enableGenerateJobFamily() {
            return this.contentGenerationSettings && this.contentGenerationSettings.enableJobFamily;
        },
        enableGenerateLevelling() {
            return this.contentGenerationSettings && this.contentGenerationSettings.enableLevelling && this.contentGenerationSettings.levels && this.contentGenerationSettings.levels.length;
        },
    },
    methods: {
        acceptImprovement() {
            this.$emit("setValue", this.improveResponseData.content, this.improveResponseData.request_id);
            this.show = false;
        },
        acceptSuggestion() {
            this.$emit("setValue", this.selectedSuggestions, this.suggestResponseData.request_id);
            this.show = false;
        },
        appendSuggestion() {
            this.$emit("setValue", `${this.data.text} ${this.selectedSuggestions[0]}`, this.suggestResponseData.request_id);
            this.show = false;
        },
        init() {
            this.textStatus = utils.applyHighlights(this.data.text, this.data.partType.wordChecks, true, false);
            if (this.enableImprove) {
                this.improveContent();
                this.tab = 0;
            }
            else if (this.enableSuggest) {
                this.suggestContent();
                this.tab = 1;
            }
            this.fetchContentGenerationSettings();
        },
        async improveContent() {
            if (this.loading) return;
            
            this.improveResponseData = null;

            this.loading = true;
            this.show = true;

            const data = {
                contentType: 'improve',
                contentTpId: this.data.partType.tmpl_part_id,
                language: this.data.docLanguage?.text ?? "English",
                spelling: this.data.spelling,
                docId: this.data.docId,
                text: this.data.text,
                additionalInstructions: this.additionalInstructions.filter(i => i)
            }
            let result = await viewUtils.generateExternalContent(data);
            this.loading = false;
            this.improveResponseData = result.data.Data;

            this.improvementTextStatus = utils.applyHighlights(this.improveResponseData.content, this.data.partType.wordChecks, true, false);
        },
        async suggestContent() {
            if (this.loading) return;

            this.suggestResponseData = null;

            this.loading = true;
            this.show = true;

            const data = {
                ...this.generateRequest,
                contentType: 'templatepart',
                contentTpId: this.data.partType.tmpl_part_id,
                language: this.data.docLanguage?.text ?? "English",
                spelling: this.data.spelling,
                docId: this.data.docId,
            }
            let result = await viewUtils.generateExternalContent(data);
            this.loading = false;
            this.suggestResponseData = result.data.Data;
            this.suggestions = result.data.Data.content;
            this.selectedSuggestions = result.data.Data.content.length === 1 ? result.data.Data.content : [];
        },
        fetchContentGenerationSettings() {
            axios
                .get("externalContent/generateSettings/" + (this.data ? this.data.docId : 0))
                .then((resp) => {
                if (resp.data.Status === "OK" && resp.data.Data) {
                    const settings = resp.data.Data;
                    this.generateRequest.jobTitle = settings.defaultJobTitle;
                    this.generateRequest.jobFamily = settings.defaultJobFamily;
                    this.generateRequest.jobLevel = settings.defaultJobLevel;
                    this.contentGenerationSettings = settings;
                }
                })
                .catch((err) => {
                    if (err.response && err.response.status === 401) {
                        this.$emit("sessionExpired", err);
                    } else {
                        alert(err.response ? err.response.data.message : err);
                    }
                    console.log(err);
                });
        }
    }
}
</script>
<style scoped lang="scss">
.regenBtn {
    position: fixed;
    z-index: 1;
    top: 0;
    right: 0;
}
</style>